import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import '../../App.scss';
import 'antd/dist/antd.css';
import {
    Space,
    Typography,
    Row, Col,
    Divider,
    Button,
    Card,
    Modal,
    Avatar,
} from 'antd';
import { PlusOutlined, TeamOutlined, PlusCircleFilled, ArrowRightOutlined } from '@ant-design/icons';
import moment from 'moment';
import firebase from 'firebase/app';

import { useTeamsList } from '../../models/useTeamsList';
import StrikeTeamsModel from '../../models/StrikeTeamsModel';
import TrackerService from '../../utils/TrackerService';

import { CreateTeamFormMinimal } from './CreateTeamFormMinimal';
import { TeamCard, IconForCCY } from './TeamDashboardComponents';
import { TeamCardTile } from './TeamCardTile';
import { useTeamDetails } from './useTeamDetails';

const { Text } = Typography;
const { Paragraph } = Typography;


export const TimeSinceFirestoreDate = ({ dateAt, ...rest }) => {
    return <Text ellipsis {...rest} >{`${moment(dateAt.toMillis()).fromNow()}`}</Text>;
}

export const LoadingText = ({ ...rest }) => {
    return <Text ellipsis {...rest} >{`Loading...`}</Text>;
}

// Custom Compact Team Card Component
export const CompactTeamCard = ({ team, clientId, isAdmin, prefixPath, locStrings, onClick, clickText, titleText }) => {
    const target = onClick ? null : `.${prefixPath || ''}/teams/${team.teamId}`;
    const teamDetails = useTeamDetails(team.teamId);
    
    // Currency symbol mapping
    const getCurrencySymbol = (currency) => {
        switch (currency) {
            case 'GBP': return '£';
            case 'EUR': return '€';
            case 'USD': return '$';
            default: return currency;
        }
    };
    
    // Limit team name length to prevent layout issues
    const truncatedTeamName = team.teamName && team.teamName.length > 20 
        ? `${team.teamName.substring(0, 20)}...` 
        : team.teamName;
    
    const buttonText = clickText || 'View Team';
    const cardTitle = titleText || 'View team';
    
    return (
        <Card
            size='small'
            style={{ 
                textAlign: 'left', 
                border: '1px solid #d9d9d9',
                background: 'white',
                height: '160px',
                display: 'flex',
                flexDirection: 'column'
            }}
            title={
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar 
                        src={team.topLogo} 
                        icon={<TeamOutlined />} 
                        size={24} 
                        style={{ borderRadius: '10%', marginRight: '8px'}} 
                    />
                </div>
            }
            extra={
                teamDetails ? (
                    <div style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        background: '#1890ff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: 'bold'
                    }}>
                        {getCurrencySymbol(teamDetails.defaultCurrency)}
                    </div>
                ) : null
            }
        >
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start' }}>
                <Text 
                    style={{ 
                        fontSize: '14px', 
                        color: '#666', 
                        textAlign: 'left',
                        lineHeight: '1.2',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        wordBreak: 'break-word'
                    }}
                >
                    {truncatedTeamName}
                </Text>
            </div>
            
            <Divider style={{ margin: '16px 0' }} />
            
            <div style={{ flex: 'none', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start' }}>
                <Button 
                    shape='round' 
                    size='large' 
                    type='primary' 
                    style={{ 
                        borderColor: '#ff0098', 
                        background: '#ff0098', 
                        borderRadius: '4px', 
                        width: '100%',
                        height: '40px'
                    }}
                    href={target}
                    onClick={onClick ? () => onClick(teamDetails || team) : null}
                >
                    {buttonText}<ArrowRightOutlined />
                </Button>
            </div>
        </Card>
    );
};

// Create Team Card Component
const CreateTeamCard = ({ clientId, client, onShowModal }) => (
    <Card
        size='small'
        style={{ 
            textAlign: 'left', 
            border: '1px solid #d9d9d9',
            background: '#fafafa',
            height: '160px',
            display: 'flex',
            flexDirection: 'column'
        }}
        title={<span style={{ fontWeight: 'bold' }}>Create Team</span>}
        extra={[
            <PlusCircleFilled key="plus-icon" style={{ fontSize: '24px', color: '#52c41a' }} />
        ]}
    >
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start' }}>
            <Text style={{ fontSize: '14px', color: '#666', textAlign: 'left' }}>Start a new team</Text>
        </div>
        
        <Divider style={{ margin: '16px 0' }} />
        
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start' }}>
            <Button
                type="primary"
                shape="default"
                size="large"
                icon={<TeamOutlined />}
                onClick={onShowModal}
                style={{
                    background: '#52c41a',
                    borderColor: '#52c41a',
                    width: '100%',
                    borderRadius: '4px',
                    height: '40px'
                }}
            >
                Create Team
            </Button>
        </div>
    </Card>
);

// Home shows at most this many; the rest are behind "All teams".
const STRIP_LIMIT = 8;
const STRIP_ITEM = { flex: '0 0 190px', maxWidth: 190, scrollSnapAlign: 'start' };

export const LoadableTeamsGrid = ({
    clientId,
    client,
    condition,
    isAdmin,
    locStrings,
}) => {

    const teams = useTeamsList(clientId);
    const history = useHistory();
    const [showCreateTeamModal, setShowCreateTeamModal] = useState(false);
    const [creatingTeam, setCreatingTeam] = useState(false);
    const [newTeamId, setNewTeamId] = useState(null);
    const [newTeamName, setNewTeamName] = useState(null);
    const [totalTeamsCount, setTotalTeamsCount] = useState(0);
    
    // Determine the correct teams URL based on current admin context
    const getTeamsUrl = () => {
        // Check if we're currently in admin interface by looking at the URL
        const currentPath = window.location.pathname;
        if (currentPath.startsWith('/admin/')) {
            return `/admin/clients/${clientId}/teams`;
        } else {
            return `/client/manage/${clientId}/teams`;
        }
    };
    
    const allTeamsLinkUrl = getTeamsUrl();

    // Fetch total teams count
    useEffect(() => {
        const fetchTotalCount = async () => {
            if (clientId) {
                const db = firebase.firestore();
                const query = db.collectionGroup('members').where('clientId', '==', clientId).where('isDeleted', '==', false);
                try {
                    const snapshot = await query.get();
                    setTotalTeamsCount(snapshot.size);
                } catch (err) {
                    // A denied read (signed out, token gone) is not a crash: the count
                    // simply stays unknown. An unhandled rejection here drew the dev
                    // overlay over every signed-out visit to a dashboard URL.
                    console.warn('[LoadableTeamsGrid] team count read failed:', err && err.message);
                }
            }
        };
        
        fetchTotalCount();
    }, [clientId]);

    const createTeam = async (data) => {
        const isLocalHost = window.location.hostname === 'localhost';
        const isTest = isLocalHost;

        setCreatingTeam(true);

        const teamName = data.teamName;
        const teamCurrency = data.defaultCurrency;

        const teamsModel = new StrikeTeamsModel({ clientId });
        const newTeamId = await teamsModel.createTeamWithDefaultSettings({ teamName, teamCurrency });

        const tracker = new TrackerService({
            isTest,
        });

        const payloadData = {
            teamOwnerClientId: clientId,
            teamName,
            teamCurrency,
        }

        tracker.mixpanelTrack("create_team", payloadData);

        if (!isTest) {
            tracker.googleTrack("create_team", payloadData);
        }

        setNewTeamId(newTeamId);
        setNewTeamName(teamName);
        setShowCreateTeamModal(false);
        setCreatingTeam(false);

        // Redirect to the new team page using React Router
        history.push(`${allTeamsLinkUrl}/${newTeamId}`);
    };

    // Always show the section if condition is met, regardless of teams count
    if (typeof condition === 'function' ? !condition() : !condition) return null;

    // Reusable header component
    const renderHeader = () => (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', paddingTop: '30px' }}>
            <Paragraph style={{ margin: 0, flex: 1, fontWeight: 'bold' }}>My Teams ({totalTeamsCount})</Paragraph>
            <button 
                type="button"
                className="btn btn-outline-dark btn-sm"
                style={{ marginLeft: '10px', fontSize: '12px', padding: '4px 8px' }}
                onClick={() => history.push(allTeamsLinkUrl)}
            >
                All teams
            </button>
        </div>
    );

    // A horizontal strip rather than a grid: home should stay the same height
    // whether you have two teams or forty. The full set lives behind "All teams",
    // where it is searchable. Mirrors the iOS home screen.
    const renderGridContainer = (children) => (
        <div
            style={{
                display: 'flex',
                gap: 16,
                overflowX: 'auto',
                paddingBottom: 8,
                scrollSnapType: 'x proximity',
                WebkitOverflowScrolling: 'touch',
            }}
        >
            {children}
        </div>
    );

    // Render loading state content
    const renderLoadingContent = () => (
        <>
            <div style={STRIP_ITEM}>
                <Card size='small' loading={true} style={{ height: '160px' }} />
            </div>
            {/* Only show Create Team card in loading state if totalTeamsCount is 0 */}
            {totalTeamsCount === 0 && (
                <div style={STRIP_ITEM}>
                    <CreateTeamCard 
                        clientId={clientId} 
                        client={client}
                        onShowModal={() => setShowCreateTeamModal(true)}
                    />
                </div>
            )}
        </>
    );

    // Render active state content
    const renderActiveContent = () => (
        <>
            {/* Show existing teams (up to 3) */}
            {teams && teams.slice(0, STRIP_LIMIT).map((team) => (
                <div key={`team-${team.teamId}`} style={STRIP_ITEM}>
                    <TeamCardTile team={team} to={`${allTeamsLinkUrl}/${team.teamId}`} />
                </div>
            ))}

            {/* Rather than silently truncating, say how many more there are. */}
            {teams && teams.length > STRIP_LIMIT && (
                <div key={'team-more'} style={STRIP_ITEM}>
                    <Card
                        hoverable
                        onClick={() => history.push(allTeamsLinkUrl)}
                        style={{ height: '240px', borderRadius: 16, borderStyle: 'dashed' }}
                        bodyStyle={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                    >
                        <Paragraph style={{ margin: 0, fontWeight: 600 }}>
                            +{teams.length - STRIP_LIMIT} more
                        </Paragraph>
                        <Paragraph type="secondary" style={{ margin: 0, fontSize: 12 }}>See all teams</Paragraph>
                    </Card>
                </div>
            )}
            
            {/* Only show Create Team card when there are zero teams */}
            {teams && teams.length === 0 && (
                <div key={'team-create'} style={STRIP_ITEM}>
                    <div style={{ height: '160px' }}>
                        <CreateTeamCard 
                            clientId={clientId} 
                            client={client}
                            onShowModal={() => setShowCreateTeamModal(true)}
                        />
                    </div>
                </div>
            )}
        </>
    );

    return (
        <div style={{ marginBottom: '30px' }}>
            {renderHeader()}
            {renderGridContainer(
                teams === null ? renderLoadingContent() : renderActiveContent()
            )}

            {/* Create Team Modal */}
            <Modal
                title="Create a team"
                visible={showCreateTeamModal}
                onCancel={() => setShowCreateTeamModal(false)}
                footer={[
                    <Button key="back" onClick={() => setShowCreateTeamModal(false)}>
                        Cancel
                    </Button>,
                ]}
            >
                <CreateTeamFormMinimal
                    callback={createTeam}
                    edit={false}
                    loading={creatingTeam}
                    client={client}
                />
                <p style={{ marginTop: '16px', textAlign: 'center', color: '#666' }}>
                    Next, you can edit and customise your new team's appearance and settings.
                </p>
            </Modal>
        </div>
    );
};

